// Toegankelijke, niet-blokkerende meldingen (port van legacy showToast).
// - Eén persistente live-region (role=status, aria-live=polite).
// - De-dup: zelfde tekst binnen 800 ms, of zelfde tekst al zichtbaar → genegeerd.
// - Schuift omhoog boven de timerbalk zolang er een missie loopt; respecteert safe-area.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { ToastApi } from './contracts';

interface ToastItem {
  id: number;
  message: string;
  leaving: boolean;
}

interface ToastLayoutApi {
  /** Zet door TimerProvider: er is een timerbalk onderin. */
  setTimerActive(active: boolean): void;
  /** Optioneel door de timerbalk-UI: exacte hoogte (px) van de balk vanaf de onderrand, of null. */
  setBottomOffset(px: number | null): void;
  /** Huidige ruimte (px) onderin die vrij moet blijven voor timerbalk en toasts; voor andere zwevende meldingen. */
  bottomPx: number;
}

const ToastContext = createContext<ToastApi | null>(null);
const ToastLayoutContext = createContext<ToastLayoutApi | null>(null);

const DEDUP_MS = 800;
const LEAVE_MS = 250;
/** Standaard ruimte boven een actieve timerbalk als de UI geen exacte hoogte doorgeeft. */
const TIMER_BAR_FALLBACK_PX = 104;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [timerActive, setTimerActive] = useState(false);
  const [bottomOffset, setBottomOffset] = useState<number | null>(null);
  const last = useRef({ message: '', at: 0 });
  const itemsRef = useRef<ToastItem[]>([]);
  const nextId = useRef(1);
  const timeouts = useRef(new Set<ReturnType<typeof setTimeout>>());

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    const set = timeouts.current;
    return () => {
      set.forEach(clearTimeout);
      set.clear();
    };
  }, []);

  const later = useCallback((fn: () => void, ms: number) => {
    const t = setTimeout(() => {
      timeouts.current.delete(t);
      fn();
    }, ms);
    timeouts.current.add(t);
  }, []);

  const showToast = useCallback(
    (message: string, durationMs = 3000) => {
      if (!message) return;
      const now = Date.now();
      if (message === last.current.message && now - last.current.at < DEDUP_MS) return;
      last.current = { message, at: now };
      if (itemsRef.current.some((t) => t.message === message && !t.leaving)) return;
      const id = nextId.current++;
      const item = { id, message, leaving: false };
      itemsRef.current = [...itemsRef.current, item];
      setItems((cur) => [...cur, item]);
      later(() => {
        setItems((cur) => cur.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
        later(() => setItems((cur) => cur.filter((t) => t.id !== id)), LEAVE_MS);
      }, Math.max(1000, durationMs));
    },
    [later],
  );

  const api = useMemo<ToastApi>(() => ({ showToast }), [showToast]);

  const offsetPx = timerActive ? (bottomOffset ?? TIMER_BAR_FALLBACK_PX) + 12 : 24;
  const layout = useMemo<ToastLayoutApi>(() => ({ setTimerActive, setBottomOffset, bottomPx: offsetPx }), [offsetPx]);
  const container = (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="false"
      className="pointer-events-none fixed inset-x-0 z-[130] flex flex-col items-center gap-2 px-4 transition-[bottom] duration-200"
      style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + ${offsetPx}px)` }}
    >
      {items.map((t) => (
        <ToastBubble key={t.id} message={t.message} leaving={t.leaving} />
      ))}
    </div>
  );

  return (
    <ToastContext.Provider value={api}>
      <ToastLayoutContext.Provider value={layout}>
        {children}
        {typeof document !== 'undefined' ? createPortal(container, document.body) : null}
      </ToastLayoutContext.Provider>
    </ToastContext.Provider>
  );
}

function ToastBubble({ message, leaving }: { message: string; leaving: boolean }) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  const visible = entered && !leaving;
  return (
    <div
      className={`max-w-md rounded-2xl border border-white/10 bg-slate-900/90 px-4 py-3 text-center text-sm font-medium text-slate-50 shadow-lg backdrop-blur-md transition-all duration-200 dark:border-white/15 dark:bg-slate-800/95 dark:text-slate-100 ${
        visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
      }`}
    >
      {message}
    </div>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast moet binnen <ToastProvider> gebruikt worden');
  return ctx;
}

/**
 * Laat de toasts boven de timerbalk zweven. TimerProvider zet `setTimerActive`;
 * de timerbalk-UI mag de exacte hoogte doorgeven met `setBottomOffset(px)`.
 */
export function useToastLayout(): ToastLayoutApi {
  const ctx = useContext(ToastLayoutContext);
  if (!ctx) throw new Error('useToastLayout moet binnen <ToastProvider> gebruikt worden');
  return ctx;
}
